/* eslint-disable jsx-a11y/anchor-is-valid */
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import {
  AdminInputPlaceholder,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  LocationVDriveEntity,
  Modal,
  PropsModal,
  StripeLogo,
  SuccessToast,
  VDriveLogo,
  useVDrive,
  setVDriveSecondaryCard,
  updateVDriveCreditCardForLocationPartner,
  reportError,
} from '@vgrubs/components';
import {
  AddressInput,
  ConfirmAddressInput,
} from 'components/src/adminPages/Deliveries/components/ManualDeliveryAddress';
import { UpdateVDriveCreditCardRequestData } from 'components/src/types/api/vDrive.types';
import { CreateManualDeliveryAddressData } from 'components/src/types/deliveries.types';
import { Dispatch, SetStateAction, useState } from 'react';
import { PaymentCreditCard } from './CreditCardComponent';

type EditCardProps = PropsModal & {
  modalIsOpen: boolean;
  id: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  handleGetVDrive: () => void;
  /**
   * Saves the backup (secondary) card instead of the default: its own route,
   * the same Stripe token, and the billing address is optional there.
   */
  secondary?: boolean;
  /** No backup card yet: the form adds one, and its title says so. */
  adding?: boolean;
};

export const EditCard = ({
  closeModal,
  modalIsOpen,
  id,
  setLoading,
  handleGetVDrive,
  secondary = false,
  adding = false,
}: EditCardProps) => {
  const PUBLISHABLE = process.env.NX_STRIPE_PUBLISHABLE_KEY as string;
  const [stripePromise] = useState(() => loadStripe(PUBLISHABLE));

  return (
    <Elements stripe={stripePromise}>
      <Form
        modalIsOpen={modalIsOpen}
        closeModal={closeModal}
        id={id}
        setLoading={setLoading}
        handleGetVDrive={handleGetVDrive}
        secondary={secondary}
        adding={adding}
      />
    </Elements>
  );
};

const Form = ({
  closeModal,
  modalIsOpen,
  id,
  setLoading,
  handleGetVDrive,
  secondary = false,
  adding = false,
}: EditCardProps) => {
  // The slot is in the title, so nobody saves a card in the wrong one.
  const label = secondary
    ? adding
      ? 'Add Backup Card'
      : 'Edit Backup Card'
    : 'Edit Card';
  const stripe = useStripe();
  const elements = useElements();

  const { locationVDrive, setLocationVDrive } = useVDrive((state) => state);

  const [changeAddressModal, setChangeAddressModal] = useState(false);
  const [confirmAddressModal, setConfirmAddressModal] =
    useState<CreateManualDeliveryAddressData | null>(null);
  const [address, setAddress] =
    useState<CreateManualDeliveryAddressData | null>(null);
  const [holderName, setHolderName] = useState('');

  const handleSetAddress = (address: any) => {
    setAddress(address);
    setConfirmAddressModal(null);
    setChangeAddressModal(false);
  };

  const locationAddress = address
    ? `${address.streetNumber} ${address.address} ${address.city} ${address.zipCode} ${address.state} ${address.country}`
    : '';

  const handleSubmitStripe = async () => {
    if (!holderName.trim()) {
      return ErrorToast('Please enter the card holder name.');
    }
    if (!address && !secondary) {
      return ErrorToast('Please enter valid billing address');
    }

    setLoading(true);

    const cardNumberElement = elements?.getElement('cardNumber');

    if (!stripe || !cardNumberElement) {
      setLoading(false);
      return;
    }

    const { token, error } = await stripe.createToken(cardNumberElement, {
      name: holderName.trim(),
    });

    if (error) {
      ErrorToast(error.message || '');
      setLoading(false);
    }

    if (token) {
      await handleUpdateVDriveCard(token.id);
    }
  };

  async function handleUpdateVDriveCard(stripeToken) {
    if (secondary) return handleSetSecondaryCard(stripeToken);

    const body: UpdateVDriveCreditCardRequestData = {
      stripeToken,
      // The vGrubs vDrive card can no longer be assigned: whatever card is
      // entered here is the owner's own.
      isVDriveCard: false,
      address: address
        ? {
            zipCode: address.zipCode,
            address: address.address,
            streetNumber: address.streetNumber,
            city: address.city,
            state: address.state,
            country: address.country,
          }
        : undefined,
    };

    try {
      const { data } = await updateVDriveCreditCardForLocationPartner(id, body);

      setLocationVDrive({
        ...(locationVDrive as LocationVDriveEntity),
        last4: data?.data?.last4 ?? '',
        isVDriveCard: false,
      } as LocationVDriveEntity);

      setAddress(null);
      setHolderName('');
      handleGetVDrive();
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
      closeModal?.();
    }
  }

  async function handleSetSecondaryCard(stripeToken: string) {
    try {
      const { data } = await setVDriveSecondaryCard(id, {
        stripeToken,
        address: address
          ? {
              zipCode: address.zipCode,
              address: address.address,
              streetNumber: address.streetNumber,
              city: address.city,
              state: address.state,
              country: address.country,
            }
          : undefined,
      });
      setAddress(null);
      setHolderName('');
      handleGetVDrive();
      SuccessToast(data.message);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
      closeModal?.();
    }
  }

  return (
    <>
      <Modal
        header={label}
        logo={false}
        closeModal={closeModal}
        isOpen={modalIsOpen}
      >
        <Div $width="100%">
          <Div $flex $center $mb="40">
            <VDriveLogo />
          </Div>
          <PaymentCreditCard name={holderName} onNameChange={setHolderName} />
          <AdminInputPlaceholder
            value={address ? locationAddress : null}
            label={secondary ? 'Billing Address (optional)' : 'Billing Address'}
            placeholder="Enter address"
            $textCenter
            onClick={() => setChangeAddressModal(true)}
          />

          <Div $flex $center $gap="8px" $mt="24">
            <Disclaimer $m="0">Powered by:</Disclaimer>
            <StripeLogo />
          </Div>

          <Button
            $selected
            $w="100%"
            $wSM="100%"
            $mt="24"
            onClick={handleSubmitStripe}
          >
            {label}
          </Button>
        </Div>
      </Modal>
      {/* //TODO make reusable component from this components */}
      <AddressInput
        modalIsOpen={changeAddressModal}
        setModalIsOpen={setChangeAddressModal}
        setLoading={setLoading}
        setConfirmAddressModal={setConfirmAddressModal}
        title={'Billing Address'}
        inputLabel={'New Billing Address'}
        placeholder={'Enter address'}
      />
      <ConfirmAddressInput
        modalIsOpen={confirmAddressModal}
        setModalIsOpen={setConfirmAddressModal}
        handleConfirmAddress={handleSetAddress}
        title={'Confirm Billing Address'}
      />
    </>
  );
};
